import { useState } from "react";
import { Mail, Globe, Phone, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import StaticPageLayout from "@/components/StaticPageLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const ROWS = [
  {
    icon: Mail,
    label: "Email",
    value: "saivigneshseva@gmail.com",
    href: "mailto:saivigneshseva@gmail.com",
    testId: "contact-email",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "+91 96263 43462",
    href: "tel:+919626343462",
    testId: "contact-phone",
  },
  {
    icon: Globe,
    label: "Website",
    value: "https://giprompthub.com",
    href: "https://giprompthub.com",
    testId: "contact-website",
  },
];

const EMPTY_FORM = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm(EMPTY_FORM);
      toast.success("Message sent", {
        description: "Thanks for reaching out — we'll reply within 24–48 hours.",
      });
    } catch (err) {
      toast.error("Could not send message", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StaticPageLayout
      eyebrow="Contact"
      title="Contact Us"
      description="Have questions, suggestions, or business inquiries? We would love to hear from you!"
      metaTitle="Contact — Giprompthub"
      metaDescription="Contact Giprompthub for questions, suggestions, or business inquiries. Response within 24–48 hours."
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Info column */}
        <div className="lg:col-span-2 space-y-6">
          <div
            className="rounded-2xl border border-white/10 bg-[#0E0E12] overflow-hidden"
            data-testid="contact-card"
          >
            {ROWS.map(({ icon: Icon, label, value, href, testId }, i) => (
              <a
                key={label}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                data-testid={testId}
                className={`flex items-center gap-4 px-6 py-5 group hover:bg-white/[0.03] transition-colors ${
                  i !== 0 ? "border-t border-white/5" : ""
                }`}
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-black/40 border border-white/10 group-hover:border-[#00E5FF]/40 transition-colors">
                  <Icon className="h-4 w-4 text-[#00E5FF]" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-mono-label text-white/50">{label}</div>
                  <div className="mt-0.5 truncate text-white group-hover:text-[#00E5FF] transition-colors">
                    {value}
                  </div>
                </div>
              </a>
            ))}
          </div>

          <div
            className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/30 px-5 py-4"
            data-testid="contact-response-note"
          >
            <Clock className="h-4 w-4 text-[#E0007A] mt-0.5 flex-shrink-0" />
            <p className="text-sm text-white/70 leading-relaxed">
              We aim to respond to all inquiries within{" "}
              <span className="text-white">24–48 hours</span>.
            </p>
          </div>
        </div>

        {/* Form column */}
        <div className="lg:col-span-3">
          <div className="rounded-2xl border border-white/10 bg-[#0E0E12] p-6 md:p-8">
            <div className="font-mono-label text-[#00E5FF]">Send a message</div>
            <h2 className="font-display text-2xl mt-1">We'd love to hear from you</h2>

            {sent ? (
              <div
                data-testid="contact-form-success"
                className="mt-6 flex items-start gap-3 rounded-xl border border-[#00E5FF]/30 bg-[#00E5FF]/5 px-5 py-4"
              >
                <CheckCircle2 className="h-5 w-5 text-[#00E5FF] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">Message received!</p>
                  <p className="text-sm text-white/60 mt-1">
                    Thanks for reaching out. We'll get back to you within 24–48 hours.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-form-send-another"
                    onClick={() => setSent(false)}
                    className="mt-3 text-sm text-[#00E5FF] hover:text-white underline underline-offset-4"
                  >
                    Send another message
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="mt-6 space-y-4"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono-label text-white/60">Name</label>
                    <input
                      required
                      maxLength={120}
                      data-testid="contact-form-name"
                      placeholder="Jane Doe"
                      className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm"
                      value={form.name}
                      onChange={update("name")}
                    />
                  </div>
                  <div>
                    <label className="font-mono-label text-white/60">Email Address</label>
                    <input
                      required
                      type="email"
                      data-testid="contact-form-email"
                      placeholder="jane@example.com"
                      className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm"
                      value={form.email}
                      onChange={update("email")}
                    />
                  </div>
                </div>
                <div>
                  <label className="font-mono-label text-white/60">Subject</label>
                  <input
                    required
                    maxLength={200}
                    data-testid="contact-form-subject"
                    placeholder="Partnership inquiry, bug report, feedback…"
                    className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm"
                    value={form.subject}
                    onChange={update("subject")}
                  />
                </div>
                <div>
                  <label className="font-mono-label text-white/60">Message</label>
                  <textarea
                    required
                    rows={6}
                    maxLength={5000}
                    data-testid="contact-form-message"
                    placeholder="Tell us what's on your mind…"
                    className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm leading-relaxed"
                    value={form.message}
                    onChange={update("message")}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-form-submit"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#E0007A] hover:bg-[#ff1493] disabled:opacity-50 text-white font-semibold px-6 py-3 glow-magenta transition-transform hover:scale-[1.01]"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {submitting ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </StaticPageLayout>
  );
}
